import type { RepeatableColumn, WorkflowField } from './templateTypes';
import type { FieldValue, RepeatableRowValue } from './values';

/*
 * Values a repeatable COLUMN carries as a whole rather than per row: which unit
 * mode it is in, and the total of its cells.
 *
 * Lifted here when `collectFilledFields` moved into this package and needed
 * them — the PDF fill maps a column's total and its "$ or %" mode into boxes
 * the advisor never types into. The app's `repeatableSum.ts` re-exports these,
 * so there is exactly ONE implementation: two copies of a sum that has to agree
 * with what a signed PDF prints is the drift worth avoiding.
 */

/** The two units a dual-unit column switches between. */
export type UnitMode = 'dollar' | 'percent';

/**
 * Value key holding one dual-unit column's current mode.
 *
 * Namespaced so it can never collide with a field id (those are GUIDs), and
 * keyed by COLUMN because the toggle governs a whole column across every row —
 * live RIF draws it once in the column header, not per row.
 */
export function unitModeKey(fieldId: string, columnId: string): string {
  return `unitMode:${fieldId}:${columnId}`;
}

/** The column's current mode, falling back to its authored default. */
export function readUnitMode(
  field: WorkflowField,
  column: RepeatableColumn,
  getValueById?: (id: string) => FieldValue,
): UnitMode {
  const authored = column.unitToggle?.defaultMode === 'percent' ? 'percent' : 'dollar';
  if (!getValueById) return authored;
  const raw = getValueById(unitModeKey(field.id, column.id));
  return raw === 'percent' || raw === 'dollar' ? raw : authored;
}

/** The symbol a `{type}` token in a column label resolves to. */
export function unitSymbol(mode: UnitMode): string {
  return mode === 'percent' ? '%' : '$';
}

/**
 * A column's display label for one language, with any `{type}` token replaced
 * by the active unit symbol.
 *
 * Exists because a dual-unit column's header otherwise contradicts its own
 * cells: Non-Reg's fund table was authored "$ Value", and ticking the toggle
 * left that header in place over a column now holding percentages. Authoring
 * it "{type} Value" lets the one label serve both modes.
 *
 * `mode` omitted resolves to the column's authored `defaultMode` — the same
 * degradation {@link readUnitMode} makes without a `getValueById`, and what the
 * handful of call sites with no answer map in reach rely on. The point is that
 * no surface can print a raw `{type}` at an advisor.
 *
 * A column with NO `unitToggle` keeps the token verbatim. Substituting `$`
 * there would quietly invent a unit the column never had; leaving it visible
 * makes the mis-authoring obvious, as `renderFilename` does with an unknown
 * token kind.
 */
export function resolveColumnLabel(
  col: RepeatableColumn,
  language: 'en' | 'fr',
  mode?: UnitMode | null,
): string {
  const label = (language === 'fr' ? col.labelFr : col.labelEn) || col.labelEn || col.labelFr || '';
  if (!col.unitToggle) return label;
  const active = mode ?? (col.unitToggle.defaultMode === 'percent' ? 'percent' : 'dollar');
  return label.replace(/\{type\}/gi, unitSymbol(active));
}

/**
 * Parse a currency-formatted number string. The FE Number input's live
 * formatter inserts thousand separators (`1,000.00`); Number.parseFloat
 * silently truncates at the first non-digit — `parseFloat('1,000.00')`
 * is 1, not 1000 — so a straight parseFloat on a cell value produces
 * false sum-mismatch and blocks Next. Strip common thousand separators
 * (comma, space, narrow-no-break-space used by fr-CA locale) before
 * handing to parseFloat.
 */
function parseCurrencyString(s: string): number {
  const cleaned = s.replace(/[,\s]/g, '');
  return Number.parseFloat(cleaned);
}

/**
 * Compute the sum of a specified column across every row of a RepeatableRow
 * field. Non-numeric cells contribute 0; empty rows contribute nothing.
 * Returns null when the field carries no sumValidation (caller signal for
 * "nothing to check").


/**
 * Compute the sum of a specified column across every row of a RepeatableRow
 * field. Non-numeric cells contribute 0; empty rows contribute nothing.
 * Returns null when the field carries no sumValidation (caller signal for
 * "nothing to check").
 */
export function computeRepeatableSum(
  field: WorkflowField,
  rows: RepeatableRowValue[],
  /** Defaults to the field's own sumValidation column; pass a column id to
   *  total a dual-unit column instead. */
  columnKey?: string,
): number | null {
  const key = columnKey ?? field.sumValidation?.columnKey;
  if (!key) return null;
  let total = 0;
  for (const row of rows) {
    const cell = row[key];
    if (typeof cell === 'number' && Number.isFinite(cell)) {
      total += cell;
    } else if (typeof cell === 'string' && cell.trim().length > 0) {
      const parsed = parseCurrencyString(cell);
      if (Number.isFinite(parsed)) total += parsed;
    }
  }
  return total;
}

/**
 * True once at least one row carries a usable number in the summed column.
 *
 * `computeRepeatableSum` cannot answer this: an untouched table and a table of
 * zeroes both total 0. The runtime uses it to hold the cross-row total back
 * until the advisor has actually typed something — a "Total: 0 / Target: 100%"
 * line in red on a table nobody has touched reads as an error the advisor
 * caused. Deliberately mirrors the cell-reading rules above (numbers, and
 * currency-formatted strings) so "counts toward the total" and "counts as data"
 * can never disagree.
 *
 * Gating is DISPLAY ONLY — `isSumSatisfied` and the Next-button check are
 * untouched, so an empty table still blocks the step exactly as before.
 */
export function hasRepeatableColumnData(
  field: WorkflowField,
  rows: RepeatableRowValue[],
  /** Defaults to the field's own sumValidation column, as above. */
  columnKey?: string,
): boolean {
  const key = columnKey ?? field.sumValidation?.columnKey;
  if (!key) return false;
  for (const row of rows) {
    const cell = row[key];
    if (typeof cell === 'number' && Number.isFinite(cell)) return true;
    if (typeof cell === 'string' && cell.trim().length > 0) {
      if (Number.isFinite(parseCurrencyString(cell))) return true;
    }
  }
  return false;
}

/**
 * How many rows of a repeatable table the advisor has actually FILLED — the
 * number a "number of funds" box on the paper form is asking for.
 *
 * Deliberately not `rows.length`. A table can be seeded with several blank rows
 * before anyone types in them (`rowsFollowFieldId` starts one table with as many
 * rows as another already has), and a blank row prints nothing at all, so
 * counting it would make the printed figure contradict the page it sits on — a
 * "3" over two filled lines.
 *
 * A row counts when ANY of its cells carries something, not just the summed one:
 * {@link hasRepeatableColumnData} answers a question about one column, while a
 * row count is about the row as a whole. Reading every cell is also what keeps
 * the count honest on a table with no numeric column at all.
 *
 * `false` is data — an unticked checkbox cell is still a cell the advisor
 * touched — but `null`, `undefined` and whitespace-only strings are not. Empty
 * arrays (an untouched CheckboxGroup cell) are empty for the same reason.
 */
export function countRepeatableRows(rows: readonly RepeatableRowValue[]): number {
  let count = 0;
  for (const row of rows) {
    if (row === null || typeof row !== 'object' || Array.isArray(row)) continue;
    if (Object.values(row).some(isFilledCell)) count++;
  }
  return count;
}

function isFilledCell(cell: unknown): boolean {
  if (cell === null || cell === undefined) return false;
  if (typeof cell === 'string') return cell.trim().length > 0;
  if (Array.isArray(cell)) return cell.length > 0;
  if (typeof cell === 'number') return Number.isFinite(cell);
  return true;
}
